import { useLocation, useNavigate } from 'react-router-dom'
import PayrollHeader from './PayrollHeader'
import { numberToIndianWords } from './utils/numberToWords'
import { exportBankLetterToExcel } from './utils/bankLetterExcel'
import { bankLetterConfig } from './config/bankLetterConfig'
import { toTitleCase } from './utils/textFormat'
import './BankLetterView.css'

function normalize(value) {
  return String(value || '').trim().toLowerCase()
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })
}

function formatRupees(value) {
  return `₹${formatNumber(value)}`
}

function formatTodayDate() {
  return new Date().toLocaleDateString('en-GB') // DD/MM/YYYY
}

// Splits the selected rows into the fixed two bank groups: SBI on its own
// letter, and every other bank pooled together on a second letter. The SBI
// letter shows Bank Name (an internal-transfer letter doesn't need IFSC);
// the Other Banks letter shows IFSC Code instead, since SBI needs it to
// route the transfer to an external bank.
// Matches by "contains sbi" (case-insensitive) rather than an exact match,
// since real bank names look like "SBI Bank", not the literal string "SBI".
function groupRowsByBank(rows) {
  const sbiRows = rows.filter((row) => normalize(row.bank_name).includes('sbi'))
  const otherRows = rows.filter((row) => !normalize(row.bank_name).includes('sbi'))

  const groups = []
  if (sbiRows.length > 0) {
    groups.push({ name: 'SBI', rows: sbiRows, columnLabel: 'Bank Name', columnField: 'bank_name' })
  }
  if (otherRows.length > 0) {
    groups.push({ name: 'Other Banks', rows: otherRows, columnLabel: 'IFSC Code', columnField: 'ifsc_code' })
  }
  return groups
}

function chunkRows(rows, size) {
  const chunks = []
  for (let i = 0; i < rows.length; i += size) {
    chunks.push(rows.slice(i, i + size))
  }
  return chunks
}

// Splits each bank group's rows into page-sized chunks - once a group's
// employees don't fit on one page, the next page repeats the full letter
// (address/salutation/body) with its own subtotal, instead of the table
// silently overflowing onto a continuation page. Page numbers are global
// across the whole document (Page 1, 2, 3...), not restarted per group.
function buildLetterPages(groups) {
  const pages = groups.flatMap((group) =>
    chunkRows(group.rows, bankLetterConfig.rowsPerPage).map((pageRows) => ({
      groupName: group.name,
      columnLabel: group.columnLabel,
      columnField: group.columnField,
      rows: pageRows,
    }))
  )
  return pages.map((page, index) => ({ ...page, pageNumber: index + 1 }))
}

function BankLetterView() {
  const location = useLocation()
  const navigate = useNavigate()
  // Employees whose Net Salary computed to zero are excluded entirely -
  // nothing to transfer, so they don't belong on a transfer letter.
  const rows = (location.state?.rows || []).filter((row) => Number(row.netSalary || 0) !== 0)
  const groups = groupRowsByBank(rows)
  const letterPages = buildLetterPages(groups)
  const todayDate = formatTodayDate()

  if (rows.length === 0) {
    return (
      <>
        <PayrollHeader />
        <div className="bank-letter-page">
          <p>No employees were selected for a Bank Letter. Please go back to the report and select at least one.</p>
          <button className="back-button" onClick={() => navigate(-1)}>
            <span aria-hidden="true">&#8592;</span> Back to Report
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <PayrollHeader className="no-print" />
      <div className="bank-letter-page">
      <style>{`
        @page {
          size: A4 portrait;
          margin: 2cm;
        }
      `}</style>

      <div className="bank-letter-toolbar no-print">
        <button className="back-button" onClick={() => navigate(-1)}>
          <span aria-hidden="true">&#8592;</span> Back to Report
        </button>
        <div className="toolbar-right">
          <button onClick={() => window.print()}>Print Bank Letter</button>
          <button onClick={() => exportBankLetterToExcel(groups)}>Export to Excel</button>
        </div>
      </div>

      {letterPages.map((page, pageIndex) => {
        const total = page.rows.reduce((sum, row) => sum + Number(row.netSalary || 0), 0)
        const words = numberToIndianWords(total)

        return (
          <div
            key={`${page.groupName}-${pageIndex}`}
            className={`bank-letter-sheet${pageIndex > 0 ? ' bank-letter-break' : ''}`}
          >
            {/* Blank space reserved for pre-printed letterhead paper - the
                organization's header occupies this space when printed on
                letterhead, so the actual content starts five lines lower. */}
            {Array.from({ length: 5 }).map((_, i) => (
              <p key={`letterhead-space-${i}`}>&nbsp;</p>
            ))}

            <div className="letter-date-row">
              <p>To,</p>
              <p className="letter-date">{todayDate}</p>
            </div>
            <p>The Manager,</p>
            <p>State Bank of India,</p>
            <p>Law University, Navanagar, Hubballi.</p>

            <p className="salutation">Hare Krishna Sir/ Madam,</p>

            <p className="letter-body">
              Kindly transfer Rs {formatNumber(total)}/- (Rupees {words}) to below mentioned SB
              accounts as per the list (Cheque No{' '.repeat(4)}________________________)
            </p>

            <table className="bank-letter-table">
              <thead>
                <tr>
                  <th>Sl No</th>
                  <th className="align-left">Account Name</th>
                  <th>Account Number</th>
                  <th>{page.columnLabel}</th>
                  <th>Net Salary</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row, index) => (
                  <tr key={row.globalIndex ?? index}>
                    <td>{index + 1}</td>
                    <td className="align-left">{toTitleCase(row.account_name)}</td>
                    <td>{row.account_number}</td>
                    <td>{row[page.columnField]}</td>
                    <td>{formatRupees(row.netSalary)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan={4} className="total-label">TOTAL</td>
                  <td>{formatRupees(total)}</td>
                </tr>
              </tbody>
            </table>

            <div className="bank-letter-footer">
              <div className="footer-left">
                <p>Thanking you,</p>
                <p>Hubballi</p>
              </div>
              <div className="footer-center">
                <p className="footer-center-spacer" aria-hidden="true">&nbsp;</p>
                <div className="signature-space"></div>
                <p className="page-label no-print">Page {page.pageNumber}</p>
              </div>
              <div className="footer-right">
                <p>Yours Sincerely,</p>
                <div className="signature-space"></div>
                <p>Authorised Sign Of ISKCON</p>
              </div>
            </div>
          </div>
        )
      })}
      </div>
    </>
  )
}

export default BankLetterView
